import {useEffect, useState} from 'react';
import Storage from '@/utils/wrappers/Storage';

type StorageValue = string | number | object | null;

function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = Storage.getItemSync(key);
      return item !== null ? item : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  useEffect(() => {
    const unsubscribe = Storage.addSubscriber(key, () => {
      try {
        const item = Storage.getItemSync(key);
        setStoredValue(item !== null ? item : initialValue);
      } catch (error) {
        console.warn(`Error syncing localStorage key "${key}":`, error);
      }
    });

    return unsubscribe;
  }, [key, initialValue]);

  const setValue = (value: T) => {
    try {
      Storage.setItem(key, value as StorageValue).catch((error) => {
        console.warn(`Error writing to localStorage key "${key}":`, error);
      });
    } catch (error) {
      console.warn(`Error writing to localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue];
}

export default useLocalStorage;
